import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import {
  QUERY_GC_TIME_MS,
  QUERY_STALE_TIME_MS,
  WORKFLOW_CATALOGUE_GC_TIME_MS,
  WORKFLOW_CATALOGUE_STALE_TIME_MS,
} from '@/config/constants'
import { workflowKeys } from '@/lib/queryKeys'
import { fetchOrgWorkflows, fetchWorkflows } from '../api'

export function useWorkflowsQuery() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: workflowKeys.list(organization?.id),
    queryFn: async () => {
      const token = await getToken()
      return fetchWorkflows(token)
    },
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}

/**
 * The org's Nova workflow catalogue (Nebula-backed), used to gate
 * capability-dependent UI such as the Encounter Note tool toggle.
 *
 * Keyed on the org alone and cached for an hour, so opening duty after duty
 * shares one request: the catalogue is deploy-time data in the nova repo, not
 * per-duty state. Prefer this over the duty-scoped `useDutyWorkflows` unless you
 * specifically need `trigger`, which only the duty listing resolves.
 */
export function useOrgWorkflowsQuery() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: workflowKeys.available(organization?.id),
    // Wait for the Clerk org so the catalogue is never cached under an
    // `undefined` org key and then refetched once the real id arrives.
    enabled: !!organization?.id,
    queryFn: async () => {
      const token = await getToken()
      return fetchOrgWorkflows(token)
    },
    staleTime: WORKFLOW_CATALOGUE_STALE_TIME_MS,
    gcTime: WORKFLOW_CATALOGUE_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
